import React from "react";
import { Input, Select, Divider, Form } from "antd";
import { STATUS_OPTIONS, LANGUAGE_OPTIONS } from "./data.d";
import { DEPARTMENT_OPTIONS } from "@/constants/contract";
import formStyles from "@/components/DynamicForm/index.module.less";
import BankInfoTable from "../components/BankInfoTable";
import { ProfileFileFormField } from "@/pages/supplier/SupplierProfile/utils/fileUploadConfig";

// ==================== 类型定义 ====================
interface UserOption {
  label: string;
  value: string;
}

// ==================== 搜索配置 ====================
export const getSearchFields = (
  inviterOptions: UserOption[],
  inviterLoading: boolean,
  onInviterSearch: (value: string) => void,
  t: (key: string) => string,
) => [
  {
    name: "supplierName",
    label: t("供应商名称"),
    component: <Input placeholder={t("请输入供应商名称")} allowClear />,
  },
  {
    name: "purchaseUserId",
    label: t("邀请人"),
    component: (
      <Select
        placeholder={t("请输入邀请人名字")}
        allowClear
        showSearch
        loading={inviterLoading}
        options={inviterOptions}
        onSearch={onInviterSearch}
        filterOption={false}
      />
    ),
  },
  {
    name: "registrationStatus",
    label: t("注册状态"),
    component: (
      <Select
        placeholder={t("请选择注册状态")}
        allowClear
        options={STATUS_OPTIONS}
      />
    ),
  },
];

// ==================== 邮箱邀请表单配置 ====================
export const getEmailInviteFormItems = (t: (key: string) => string) => [
  {
    label: t("供应商名称"),
    name: "supplierName",
    col: { span: 12 },
    rules: [{ required: true, message: t("请输入供应商名称") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
      allowClear: true,
    },
  },
  {
    label: t("语言"),
    name: "language",
    col: { span: 12 },
    initialValue: "zh_HK",
    widget: {
      widgetType: "Select",
      placeholder: t("请选择"),
      options: [
        { label: "繁體中文", value: "zh_HK" },
        { label: "简体中文", value: "zh_CN" },
        { label: "English", value: "en_US" },
      ],
    },
  },
  {
    label: t("供应商邮箱"),
    name: "supplierEmail",
    col: { span: 24 },
    rules: [
      { required: true, message: t("请输入供应商邮箱") },
      { type: "email", message: t("邮箱格式不正确") },
    ],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
      allowClear: true,
    },
  },
  {
    label: t("邮件主题"),
    name: "emailTheme",
    col: { span: 24 },
    rules: [{ required: true, message: t("请输入邮件主题") }],
    widget: {
      widgetType: "Input",
      placeholder: t("请输入"),
      allowClear: true,
    },
  },
  {
    label: t("邀请文本"),
    name: "inviteContent",
    col: { span: 24 },
    rules: [{ required: true, message: t("请输入邮请文本") }],
    widget: {
      widgetType: "RichTextEditor",
      config: {
        initialFrameHeight: 350,
      },
    },
  },
];

// ==================== 代注册表单配置 ====================
export const getRegisterFormItems = (
  mode?: "register" | "edit" | "audit" | "view",
  isFromWorkbench?: boolean,
  countryMap?: Record<string, any>,
  t?: (key: string) => string,
) => [
  {
    name: "custom2",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.sectionHeaderWithAction}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div className={formStyles.sectionIndicator} />
            <span className={formStyles.sectionTitle}>
              {t?.("供应商基本信息") || "供应商基本信息"}
            </span>
          </div>
          {/* <div
            className={formStyles.sectionAction}
            onClick={() => {
              console.log("查看更多供应商信息");
            }}
          >
            <img
              src={qichachaIcon}
              alt="企查查"
              className={localStyles.qichachaIcon}
            />
            <span className={formStyles.actionText}>查看更多供应商信息</span>
            <RightOutlined className={formStyles.actionIcon} />
          </div> */}
        </div>
      ),
    },
  },
  {
    name: "supplierWrapperStart",
    col: { span: 24 },
    wrapperStart: { className: formStyles.wrapperLight },
  },
  {
    label: t?.("供应商名称（中文）") || "供应商名称（中文）",
    name: "supplierName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [
      {
        required: true,
        message: t?.("请输入供应商名称") || "请输入供应商名称",
      },
    ],
    widget: {
      widgetType: "AutoComplete",
      placeholder:
        t?.("请输入供应商名称或从下拉选择") || "请输入供应商名称或从下拉选择",
      allowClear: true,
      options: [],
      disabled: mode === "view",
    },
  },
  {
    label: t?.("供应商名称（英文）") || "供应商名称（英文）",
    name: "supplierUsName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    widget: {
      widgetType: "Input",
      disabled: mode === "view",
      placeholder: t?.("请输入供应商名称") || "请输入供应商名称",
    },
  },
  {
    label: t?.("营业有效期") || "营业有效期",
    name: "supplierDateRange",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [
      {
        required: true,
        message: t?.("请选择营业有效期") || "请选择营业有效期",
      },
    ],
    widget: {
      widgetType: "DatePicker.RangePicker",
      disabled: mode === "view",
      placeholder: [
        t?.("开始日期") || "开始日期",
        t?.("结束日期") || "结束日期",
      ],
      style: { width: "100%" },
    },
  },
  {
    label: t?.("统一社会信用代码/商业登记号") || "统一社会信用代码/商业登记号",
    name: "socialCreditCode",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    widget: {
      widgetType: "Input",
      disabled: mode === "view",
      readOnly: mode !== "view",
      placeholder: t?.("由供应商名称自动填充") || "由供应商名称自动填充",
    },
  },
  {
    label: t?.("企业类型") || "企业类型",
    name: "supplierType",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [
      { required: true, message: t?.("请选择企业类型") || "请选择企业类型" },
    ],
    widget: {
      widgetType: "Select",
      placeholder: t?.("请选择") || "请选择",
      disabled: mode === "view",
      options: [
        { label: t?.("生产商") || "生产商", value: 1 },
        { label: t?.("代理商") || "代理商", value: 2 },
      ],
    },
  },
  {
    label: t?.("生产商名称") || "生产商名称",
    name: "manufacturerName",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [
      {
        required: true,
        message: t?.("请输入或选择生产商名称") || "请输入或选择生产商名称",
      },
    ],
    widget: {
      widgetType: "AutoComplete",
      placeholder:
        t?.("请输入生产商名称或从下拉选择") || "请输入生产商名称或从下拉选择",
      allowClear: true,
      disabled: mode === "view",
      options: [],
    },
    isShow: (formValues: any) => formValues?.supplierType === 2,
  },
  {
    name: "divider3",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.subSectionTitle}>
          {t?.("地址信息") || "地址信息"}
        </div>
      ),
    },
  },
  {
    label: t?.("国家") || "国家",
    name: "supplierCountry",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t?.("请选择国家") || "请选择国家" }],
    widget: {
      widgetType: "Select",
      placeholder: t?.("请选择") || "请选择",
      disabled: mode === "view",
    },
  },
  {
    label: t?.("省份/城市") || "省份/城市",
    name: "supplierCity",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [
      { required: true, message: t?.("请选择省份/城市") || "请选择省份/城市" },
    ],
    widget: {
      widgetType: "Cascader",
      placeholder: t?.("请选择省份/城市") || "请选择省份/城市",
      options: [],
      showSearch: true,
      changeOnSelect: true,
      allowClear: true,
      expandTrigger: "hover",
      disabled: mode === "view",
    },
    isShow: (formValues: any) => {
      const selectedCountryCode = formValues?.supplierCountry;
      if (!selectedCountryCode || !countryMap) {
        return true;
      }
      const selectedCountry = countryMap[selectedCountryCode];
      return selectedCountry?.areaCode === 156;
    },
  },
  {
    label: t?.("省份/城市") || "省份/城市",
    name: "supplierCity",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [
      { required: true, message: t?.("请输入省份/城市") || "请输入省份/城市" },
    ],
    widget: {
      widgetType: "Input",
      placeholder: t?.("请输入省份/城市") || "请输入省份/城市",
      allowClear: true,
      disabled: mode === "view",
    },
    isShow: (formValues: any) => {
      const selectedCountryCode = formValues?.supplierCountry;
      if (!selectedCountryCode || !countryMap) {
        return false;
      }
      const selectedCountry = countryMap[selectedCountryCode];
      return selectedCountry?.areaCode !== 156;
    },
  },
  {
    label: t?.("邮编") || "邮编",
    name: "supplierPostalCode",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    widget: {
      widgetType: "Input",
      placeholder: t?.("请输入") || "请输入",
      disabled: mode === "view",
    },
  },
  {
    label: t?.("街道") || "街道",
    name: "supplierStreet",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t?.("请输入街道") || "请输入街道" }],
    widget: {
      widgetType: "Input.TextArea",
      placeholder: t?.("请输入") || "请输入",
      rows: 2,
      style: { resize: "none" },
      disabled: mode === "view",
    },
  },
  {
    label: t?.("语言") || "语言",
    name: "supplierLanguage",
    col: { span: 8 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    className: "vertical-label-item",
    rules: [{ required: true, message: t?.("请选择语言") || "请选择语言" }],
    widget: {
      widgetType: "Select",
      placeholder: t?.("请选择") || "请选择",
      options: LANGUAGE_OPTIONS,
      disabled: mode === "view",
    },
  },
  {
    name: "divider4",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.subSectionTitle}>
          {t?.("供货信息") || "供货信息"}
        </div>
      ),
    },
  },
  {
    label: t?.("经营范围") || "经营范围",
    name: "supplyInfo",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 20 },
    rules: [
      { required: true, message: t?.("请输入经营范围") || "请输入经营范围" },
    ],
    widget: {
      widgetType: "Input.TextArea",
      placeholder:
        "计算机软硬件、网络设备及电子商务平台的研究开发、技术咨询、技术转让、技术服务;系统集成、技术转让。（以上各项涉及法律、行政法规、国务院决定禁止的项目除外,限制的项目须取得许可后方可经营）...",
      rows: 3,
      style: { resize: "none" },
      disabled: mode === "view",
    },
  },
  {
    name: "divider5",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.subSectionTitle}>
          {t?.("注册人信息") || "注册人信息"}
        </div>
      ),
    },
  },
  {
    name: "registrantList",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (
        _fieldName: any,
        _remove: any,
        fieldValue: any,
        { formRef }: any,
      ) => {
        const registrantList = fieldValue || [];

        const handleFieldChange = (
          index: number,
          field: string,
          value: any,
        ) => {
          const newList = [...registrantList];
          newList[index] = {
            ...newList[index],
            [field]: value,
          };
          formRef?.current?.setFieldsValue({ registrantList: newList });
          // 手动触发 onValuesChange 以确保 DynamicForm 状态更新
          if (window.__dynamicFormOnValuesChange) {
            const allValues = formRef?.current?.getFieldsValue();
            window.__dynamicFormOnValuesChange(
              { registrantList: newList },
              allValues,
            );
          }
        };

        return (
          <div>
            {registrantList.map((registrant: any, index: number) => (
              <div
                key={index}
                style={{
                  marginBottom: index < registrantList.length - 1 ? 32 : 0,
                }}
              >
                {/* 第一行：姓氏、名字、所属部门 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "16px",
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: "#ff4d4f", marginRight: 4 }}>
                        *
                      </span>
                      {t?.("姓氏") || "姓氏"}
                    </div>
                    <Form.Item
                      name={["registrantList", index, "surname"]}
                      rules={[
                        {
                          required: true,
                          message: t?.("请输入姓氏") || "请输入姓氏",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={t?.("请输入") || "请输入"}
                        onChange={(e) =>
                          handleFieldChange(index, "surname", e.target.value)
                        }
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: "#ff4d4f", marginRight: 4 }}>
                        *
                      </span>
                      {t?.("名字") || "名字"}
                    </div>
                    <Form.Item
                      name={["registrantList", index, "name"]}
                      rules={[
                        {
                          required: true,
                          message: t?.("请输入名字") || "请输入名字",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={t?.("请输入") || "请输入"}
                        onChange={(e) =>
                          handleFieldChange(index, "name", e.target.value)
                        }
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <div style={{ marginBottom: 8 }}>
                      {t?.("所属部门") || "所属部门"}
                    </div>
                    <Form.Item
                      name={["registrantList", index, "deptCode"]}
                      style={{ marginBottom: 0 }}
                    >
                      <Select
                        placeholder={t?.("请选择部门") || "请选择部门"}
                        options={DEPARTMENT_OPTIONS}
                        onChange={(value) =>
                          handleFieldChange(index, "deptCode", value)
                        }
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* 第二行：电话号码、注册邮箱 */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "16px",
                  }}
                >
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: "#ff4d4f", marginRight: 4 }}>
                        *
                      </span>
                      {t?.("电话号码") || "电话号码"}
                    </div>
                    <Form.Item
                      name={["registrantList", index, "phone"]}
                      rules={[
                        {
                          required: true,
                          message: t?.("请输入电话号码") || "请输入电话号码",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={t?.("请输入") || "请输入"}
                        onChange={(e) =>
                          handleFieldChange(index, "phone", e.target.value)
                        }
                      />
                    </Form.Item>
                  </div>

                  <div>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: "#ff4d4f", marginRight: 4 }}>
                        *
                      </span>
                      {t?.("注册邮箱") || "注册邮箱"}
                    </div>
                    <Form.Item
                      name={["registrantList", index, "email"]}
                      rules={[
                        {
                          required: true,
                          message: t?.("请输入注册邮箱") || "请输入注册邮箱",
                        },
                        {
                          type: "email",
                          message: t?.("邮箱格式不正确") || "邮箱格式不正确",
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder={t?.("请输入") || "请输入"}
                        onChange={(e) =>
                          handleFieldChange(index, "email", e.target.value)
                        }
                      />
                    </Form.Item>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      },
    },
  },
  {
    name: "divider6",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.subSectionTitle}>
          {t?.("银行信息") || "银行信息"}
        </div>
      ),
    },
  },
  {
    name: "bankList",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (
        _fieldName: any,
        _remove: any,
        fieldValue: any,
        { formRef }: any,
      ) => {
        // 确保 fieldValue 是数组
        const bankListValue = Array.isArray(fieldValue) ? fieldValue : [];
        // 从全局变量获取当前模式，用于判断是否为只读
        const currentMode =
          (window as any).__registrationModalMode || "register";
        const isReadOnly = currentMode === "view";

        return (
          <BankInfoTable
            key={`bank-info-table-${bankListValue.length}`}
            value={bankListValue}
            readOnly={isReadOnly}
            onChange={(newValue) => {
              if (formRef?.current) {
                formRef.current.setFieldsValue({ bankList: newValue });
                // 手动触发 onValuesChange 以确保 DynamicForm 状态更新
                if (window.__dynamicFormOnValuesChange) {
                  const allValues = formRef.current.getFieldsValue();
                  window.__dynamicFormOnValuesChange(
                    { bankList: newValue },
                    allValues,
                  );
                }
              }
            }}
          />
        );
      },
    },
  },
  {
    name: "divider7",
    col: { span: 24 },
    custom: {
      render: () => (
        <div className={formStyles.subSectionTitle}>
          {t?.("证照文件") || "证照文件"}
        </div>
      ),
    },
  },
  {
    label: t?.("营业执照") || "营业执照",
    name: "businessLicense",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (_fieldName: any, _remove: any, fieldValue: any) => {
        const currentMode =
          (window as any).__registrationModalMode || "register";
        const isReadOnly = currentMode === "view";
        return (
          <ProfileFileFormField
            fieldName="businessLicense"
            initialFiles={fieldValue || []}
            required={false}
            readOnly={isReadOnly}
          />
        );
      },
    },
  },
  {
    label: t?.("银行资料") || "银行资料",
    name: "bankDocs",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (_fieldName: any, _remove: any, fieldValue: any) => {
        const currentMode =
          (window as any).__registrationModalMode || "register";
        const isReadOnly = currentMode === "view";
        return (
          <ProfileFileFormField
            fieldName="bankDocs"
            initialFiles={fieldValue || []}
            required={false}
            readOnly={isReadOnly}
          />
        );
      },
    },
  },
  {
    label: t?.("开户证明") || "开户证明",
    name: "BAC",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (_fieldName: any, _remove: any, fieldValue: any) => {
        const currentMode =
          (window as any).__registrationModalMode || "register";
        const isReadOnly = currentMode === "view";
        return (
          <ProfileFileFormField
            fieldName="BAC"
            initialFiles={fieldValue || []}
            required={false}
            readOnly={isReadOnly}
          />
        );
      },
    },
  },
  {
    label: t?.("收款授权信") || "收款授权信",
    name: "PCAL",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (_fieldName: any, _remove: any, fieldValue: any) => {
        const currentMode =
          (window as any).__registrationModalMode || "register";
        const isReadOnly = currentMode === "view";
        return (
          <ProfileFileFormField
            fieldName="PCAL"
            initialFiles={fieldValue || []}
            required={false}
            readOnly={isReadOnly}
          />
        );
      },
    },
  },
  {
    label: t?.("其他") || "其他",
    name: "other",
    col: { span: 24 },
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
    custom: {
      render: (_fieldName: any, _remove: any, fieldValue: any) => {
        const currentMode =
          (window as any).__registrationModalMode || "register";
        const isReadOnly = currentMode === "view";
        return (
          <ProfileFileFormField
            fieldName="other"
            initialFiles={fieldValue || []}
            required={false}
            readOnly={isReadOnly}
          />
        );
      },
    },
  },
  {
    name: "supplierWrapperEnd",
    col: { span: 24 },
    wrapperEnd: true,
  },
  // ==================== 审核状态字段（隐藏） ====================
  {
    name: "registerStatus",
    col: { span: 24 },
    widget: {
      widgetType: "Input",
      style: { display: "none" },
    },
  },
  {
    name: "divider8",
    col: { span: 24 },
    custom: {
      render: () => <Divider />,
    },
    isShow: () => mode === "audit",
  },
  {
    label: t?.("审批意见") || "审批意见",
    name: "auditStatus",
    col: { span: 24 },
    labelCol: { span: 2 },
    wrapperCol: { span: 18 },
    rules: [
      { required: true, message: t?.("请选择审批意见") || "请选择审批意见" },
    ],
    widget: {
      widgetType: "Radio.Group",
      options: [
        { label: t?.("审核通过") || "审核通过", value: 1 },
        { label: t?.("驳回") || "驳回", value: 2 },
      ],
    },
    isShow: () => mode === "audit",
  },
  {
    label: t?.("意见说明") || "意见说明",
    name: "auditOpinion",
    col: { span: 24 },
    labelCol: { span: 2 },
    wrapperCol: { span: 18 },
    rules: [
      {
        validator: (_: any, value: any, callback: any) => {
          // 这个验证器会在表单值变化时被调用
          // 需要通过 formValues 来判断 auditStatus 的值
          callback();
        },
      },
    ],
    widget: {
      widgetType: "Input.TextArea",
      placeholder: t?.("请输入意见说明") || "请输入...",
      rows: 3,
      style: { resize: "none" },
    },
    isShow: () => mode === "audit",
    // 动态验证规则：当 auditStatus 为 2（驳回）时，auditOpinion 必填
    dynamicRules: (formValues: any) => {
      if (formValues?.auditStatus === 2) {
        return [
          {
            required: true,
            message: t?.("驳回时意见说明为必填") || "驳回时意见说明为必填",
          },
        ];
      }
      return [];
    },
  },
];
